import {
  SubcontractorField,
  useSubcontractorFormOperationsProps,
} from "@/pages/contractor-profile/subcontractor/edit/types";
import { toast } from "@/components";
import {
  CONTRACTORS_LICENCE_DATE_FORMAT,
  SUB_CONTRACTOR_PAGE_ROOT_PATH,
  SubcontractorPage,
  TOAST_DEFAULT_DURATION,
} from "@/constants";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useState } from "react";
import { AuthContext } from "@/guards/auth/auth-context";
import { getFileFormat } from "@/utils/get-file-format";
import { getReasonPhrase, StatusCodes } from "http-status-codes";
import {
  requestCreateSubcontractor,
  requestDeleteSubcontractor,
  requestUpdateSubcontractor,
} from "@/services";
import { format } from "date-fns";

export const useSubcontractorFormOperations = ({
  getValues,
  ownCompanyNo,
  email,
  contractorId,
  adcciMembershipNo,
  docTypeId,
}: useSubcontractorFormOperationsProps) => {
  const { account } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEdit = !!id;

  const handleSubmit = async () => {
    const toastId = toast.info("Submitting subcontractor...", {
      duration: TOAST_DEFAULT_DURATION * 5,
    });

    const formData = getValues();

    try {
      const controller = new AbortController();

      const request = isEdit
        ? requestUpdateSubcontractor
        : requestCreateSubcontractor;

      setIsSubmitting(true);

      await request(
        {
          email: account?.username ?? "",
        },
        {
          Email: email,
          CompanyNo: formData[SubcontractorField.COMPANY_ID] ?? ownCompanyNo,
          SubContractorNameEn: formData[SubcontractorField.ENGLISH_NAME],
          SubContractorNameAr: formData[SubcontractorField.ARABIC_NAME],
          SubContractorId: id,
          CicpaId: Number(formData[SubcontractorField.CICPA_ID]) || 0,
          PhoneNumber: formData[SubcontractorField.PHONE],
          TradeLicenseNumber: formData[SubcontractorField.ADDCI],
          TradeLicenseExpiryDate: format(
            formData[SubcontractorField.TRADE_LICENSE_EXPIRY],
            CONTRACTORS_LICENCE_DATE_FORMAT,
          ),
          ContractorId: Number(contractorId),
          AdcciMembershipNo:
            Number(formData[SubcontractorField.ADDCI]) ?? +adcciMembershipNo,
          TradeLicenseDocument: {
            DocTypeId: docTypeId,
            FileName: formData[SubcontractorField.TRADE_LICENSE_FILE_NAME],
            DocNameEn: formData[SubcontractorField.TRADE_LICENSE_FILE_NAME],
            Format:
              getFileFormat(
                formData[SubcontractorField.TRADE_LICENSE_FILE_FORMAT],
              ) || formData[SubcontractorField.TRADE_LICENSE_FILE_FORMAT],
            FileBase64: formData[SubcontractorField.TRADE_LICENSE],
            Value: formData.Value,
          },
        },
        { signal: controller.signal },
      );

      toast.success("Subcontractor form is saved", {
        id: toastId,
        duration: TOAST_DEFAULT_DURATION,
      });
      navigate(`${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.List}`);
      setIsSubmitting(false);
    } catch (error) {
      const msg =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        error?.message ?? getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR);

      toast.error(msg, {
        id: toastId,
        duration: TOAST_DEFAULT_DURATION,
      });
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const toastId = toast.info("Deleting subcontractor...");

    if (id) {
      setIsSubmitting(true);

      try {
        const controller = new AbortController();

        await requestDeleteSubcontractor(
          { email: account?.username ?? "", subContractorId: id },
          { signal: controller.signal },
        );

        toast.success("Subcontractor was deleted", {
          id: toastId,
          duration: TOAST_DEFAULT_DURATION,
        });

        navigate(`${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.List}`);
        setIsSubmitting(false);
      } catch (error) {
        const msg =
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          error?.message ?? getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR);

        toast.error(msg, {
          id: toastId,
          duration: TOAST_DEFAULT_DURATION,
        });
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    navigate(`${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.List}`);
  };

  return {
    isSubmitting,
    onSubmit: handleSubmit,
    onDelete: handleDelete,
    onCancel: handleCancel,
  };
};
